import React, {FC, memo, useState} from "react";
import {Alert, Button, Input, Modal, Popconfirm, Space, Tag, Tooltip} from "antd";
import {DeleteFilled, EditOutlined, InfoCircleOutlined, PlusCircleOutlined, SaveOutlined} from "@ant-design/icons";
import {
    GetBookingByIdDocument,
    GetBookingByIdQuery,
    GetBookingByIdWithCourseLessonBookingsDocument,
    GetBookingByIdWithCourseLessonBookingsQuery,
    GetPaidScheduledPaymentsDocument,
    GetUnpaidScheduledPaymentsDocument,
    PaymentProvider,
    ScheduledPaymentType,
    useUpdateSettledBookingDiscountMutation,
} from "../../generated/graphql";
import {RenderAmount} from "../TableColumnRenderers/TableColumnRenderers";
import BookingDiscountDisplay from "./BookingDiscountDisplay";
import MoneyInput from "../MoneyInput/MoneyInput";
import {erasedRecordMessage} from "../../helpers/erasedRecordMessage";
import {useCurrency} from "../../helpers/currencyHelper";
import {CurrencyShort} from "../../helpers/renderAmount";
import {remainingDiscountNameLength, stackDiscount} from "../../containers/bookings/CreateEditBookings/BookingSteps/BookingDiscount";

type PossibleBookingType =
    | GetBookingByIdQuery["bookingById"]
    | GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"];

type SettledBookingDiscountProps = {
    booking?: PossibleBookingType;
    bookingId: string;
    allPaid: boolean;
    setTimeStamp?: (ts: number) => void;
};

const SettledBookingDiscount: FC<SettledBookingDiscountProps> = ({booking, bookingId, allPaid, setTimeStamp}) => {
    const [modalOpen, setModalOpen] = useState(false);
    // Weiterer Rabatt wie im Buchungs-Editor (#1627): Betrag kommt obendrauf, Bezeichnungen mit Einzelbeträgen.
    const [adding, setAdding] = useState(false);
    const currency = useCurrency().short as CurrencyShort;
    const discount = booking?.discount ?? 0;
    const discountName = booking?.discountName ?? "";
    // Wie im Buchungs-Editor (BookingDiscount): Entfernen oder Ändern löst die Einlösung, der Server nullt discountCodeId.
    const linkedCodeText = booking?.hasDiscountCode
        ? `Die Buchung zählt danach nicht mehr als Einlösung von „${discountName || "Rabattcode"}“. Bei einer Höchstzahl wird der Platz wieder frei.`
        : undefined;

    const [editDiscountName, setEditDiscountName] = useState(discountName || "Rabatt");
    const [editDiscountAmount, setEditDiscountAmount] = useState(discount);

    const [updateDiscount, {loading}] = useUpdateSettledBookingDiscountMutation({
        refetchQueries: [
            {query: GetBookingByIdDocument, variables: {id: bookingId}},
            {query: GetBookingByIdWithCourseLessonBookingsDocument, variables: {id: bookingId}},
            {query: GetUnpaidScheduledPaymentsDocument, variables: {bookingId, options: {}}},
            {query: GetPaidScheduledPaymentsDocument, variables: {bookingId, options: {}}},
        ],
        awaitRefetchQueries: true,
    });

    // Wenn alle Posten bezahlt -> read-only Fallback
    if (allPaid) {
        return <BookingDiscountDisplay booking={booking} />;
    }

    // Phase 15 / LESSB-04 / D-10 — Defense-in-Depth UI-Disable:
    // Wenn mindestens eine Rate bereits online (Mollie) bezahlt ist, darf kein Rabatt
    // mehr geaendert werden. Der Backend-Guard in settledBookingDiscount.service.ts wirft
    // ForbiddenError bei Umgehung; dieses UI-Disable ist die benutzerfreundliche erste Schicht.
    const hasPaidMollieSP = (booking?.scheduledPayments ?? []).some(
        (sp) =>
            sp.paymentDone !== null
            && sp.paymentDone !== undefined
            && sp.paymentProvider === PaymentProvider.Mollie,
    );

    if (hasPaidMollieSP) {
        return (
            <Tooltip title="Bezahlte Online-Raten können nicht rabattiert werden — Refund in Phase 14">
                <Button type="dashed" size="small" icon={<PlusCircleOutlined />} disabled>
                    {"Rabatt hinzufügen"}
                </Button>
            </Tooltip>
        );
    }

    // Teilbezahlte offene Rate: Backend würde openAmount auf den neuen Vollen Betrag setzen
    // und bereits geflossenes Geld still wieder öffnen.
    const hasPartiallyPaidOpenSP = (booking?.scheduledPayments ?? []).some((sp) => {
        if (sp.paymentDone != null || sp.openAmount == null || sp.paymentAmount == null) {
            return false;
        }
        const openAmount = Number(sp.openAmount);
        const paymentAmount = Number(sp.paymentAmount);

        return openAmount > 0 && openAmount < paymentAmount;
    });
    const partialPaidBlockTitle =
        "Teilbezahlte offene Raten können nicht automatisch rabattiert werden — bitte die Teilzahlung zuerst abschließen.";

    // Max-Rabatt berechnen: Summe unbezahlter Standard-Posten + bestehende Abzuege.
    // Beide Buchungs-Queries liefern Raten mit unterschiedlichen Feldern; auf der Union der Arrays ist .filter nicht aufrufbar.
    const scheduledPaymentRows: Array<{type?: ScheduledPaymentType | null; paymentDone?: unknown; paymentAmount?: number | null}> = booking?.scheduledPayments ?? [];
    const unpaidStandardSum = scheduledPaymentRows
        .filter((p) => p.type === ScheduledPaymentType.Standard && p.paymentDone === null)
        .reduce((sum, p) => sum + (p.paymentAmount ?? 0), 0);
    const maxDiscount = unpaidStandardSum + (booking?.discount ?? 0) - (booking?.memberDiscount ?? 0) - (booking?.creditedBalance ?? 0);

    const showCorrectionInvoiceHint = () => {
        Modal.info({
            title: "Hinweis: Korrekturrechnung",
            content: "Die Buchungsbeträge wurden angepasst. Bitte prüfe, ob eine Korrekturrechnung erstellt und dem Bucher zugeschickt werden muss, damit dieser eine aktuelle Rechnung erhält.",
        });
    };

    // maxDiscount enthält den bisherigen Rabatt; ein weiterer darf nur den Rest kürzen.
    const modalMaxDiscount = Math.max(0, adding ? maxDiscount - discount : maxDiscount);

    const handleSave = async () => {
        const next = adding
            ? stackDiscount({discount, discountName}, {discount: Math.min(editDiscountAmount, modalMaxDiscount), discountName: editDiscountName}, currency)
            : {discount: editDiscountAmount, discountName: editDiscountName};

        try {
            await updateDiscount({
                variables: {
                    bookingId,
                    ...next,
                },
            });
            setTimeStamp?.(Date.now());
            setModalOpen(false);
            showCorrectionInvoiceHint();
        } catch (error) {
            Modal.error({
                title: "Fehler",
                content: erasedRecordMessage(error) ?? "Der Rabatt konnte nicht gespeichert werden. Bitte versuche es erneut.",
            });
        }
    };

    const handleDelete = async () => {
        try {
            await updateDiscount({
                variables: {
                    bookingId,
                    discount: 0,
                    discountName: "",
                },
            });
            setTimeStamp?.(Date.now());
            showCorrectionInvoiceHint();
        } catch (error) {
            Modal.error({
                title: "Fehler",
                content: erasedRecordMessage(error) ?? "Der Rabatt konnte nicht entfernt werden.",
            });
        }
    };

    const openEditModal = () => {
        setAdding(false);
        setEditDiscountName(discountName || "Rabatt");
        setEditDiscountAmount(discount);
        setModalOpen(true);
    };

    const openAddModal = () => {
        setAdding(true);
        setEditDiscountName("");
        setEditDiscountAmount(0);
        setModalOpen(true);
    };

    // Rabatt vorhanden -> Badge mit Edit/Delete
    if (discount > 0) {
        const label = discountName || "Rabatt";

        return (
            <>
                {renderDiscountModal()}
                <Tag color="green">
                    <Space size={5}>
                        <span>{label}</span>
                        <RenderAmount amount={discount} style={{fontSize: "unset", fontWeight: 600}} />
                        {hasPartiallyPaidOpenSP ? (
                            <Tooltip title={partialPaidBlockTitle}>
                                <EditOutlined style={{color: "#d9d9d9", cursor: "not-allowed"}} />
                            </Tooltip>
                        ) : (
                            <Tooltip title="Rabatt bearbeiten">
                                <EditOutlined style={{cursor: "pointer"}} onClick={openEditModal} />
                            </Tooltip>
                        )}
                        {/* Rabattcode-Einlösung nicht aufstocken, wie im Buchungs-Editor: eine Änderung löst die Einlösung. */}
                        {!hasPartiallyPaidOpenSP && !booking?.hasDiscountCode && maxDiscount - discount > 0 ? (
                            <Tooltip title="Weiteren Rabatt hinzufügen">
                                <PlusCircleOutlined style={{cursor: "pointer"}} onClick={openAddModal} />
                            </Tooltip>
                        ) : null}
                        {hasPartiallyPaidOpenSP ? (
                            <Tooltip title={partialPaidBlockTitle}>
                                <DeleteFilled style={{color: "#d9d9d9", cursor: "not-allowed"}} />
                            </Tooltip>
                        ) : (
                            <Popconfirm
                                title={
                                    linkedCodeText ? (
                                        <div style={{maxWidth: 320}}>
                                            <div style={{fontWeight: 600}}>{"Rabattcode-Rabatt entfernen?"}</div>
                                            <div>{linkedCodeText}</div>
                                        </div>
                                    ) : (
                                        "Rabatt wirklich entfernen?"
                                    )
                                }
                                onConfirm={handleDelete}
                                okText="Ja, entfernen"
                                okType="danger"
                                cancelText="Abbrechen"
                            >
                                <Tooltip title="Rabatt entfernen">
                                    <DeleteFilled style={{cursor: "pointer", color: "#ff4d4f"}} />
                                </Tooltip>
                            </Popconfirm>
                        )}
                    </Space>
                </Tag>
            </>
        );
    }

    // Kein Rabatt -> Button zum Hinzufuegen
    if (hasPartiallyPaidOpenSP) {
        return (
            <Tooltip title={partialPaidBlockTitle}>
                <Button type="dashed" size="small" icon={<PlusCircleOutlined />} disabled>
                    {"Rabatt hinzufügen"}
                </Button>
            </Tooltip>
        );
    }

    return (
        <>
            {renderDiscountModal()}
            <Button type="dashed" size="small" icon={<PlusCircleOutlined />} onClick={openEditModal}>
                {"Rabatt hinzufügen"}
            </Button>
        </>
    );

    function renderDiscountModal() {
        return (
            <Modal
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                title={adding ? "Weiteren Rabatt hinzufügen" : discount > 0 ? "Rabatt bearbeiten" : "Rabatt hinzufügen"}
                okText="Speichern"
                onOk={handleSave}
                okButtonProps={{loading, icon: <SaveOutlined />, disabled: editDiscountAmount <= 0}}
                cancelButtonProps={{disabled: loading}}
                destroyOnClose
            >
                <div style={{display: "flex", flexDirection: "column", gap: 12}}>
                    {linkedCodeText && discount > 0 && !adding ? (
                        <Alert type="warning" showIcon message={"Rabatt aus einem Rabattcode"} description={`Wer Betrag oder Bezeichnung ändert, löst die Verknüpfung. ${linkedCodeText}`} />
                    ) : null}
                    {adding ? (
                        <div>
                            {"Bisheriger Rabatt: "}
                            <b>{discountName || "Rabatt"}</b> <RenderAmount amount={discount} style={{fontSize: "unset"}} />
                        </div>
                    ) : null}
                    <div>
                        <label>{"Bezeichnung"}</label>
                        <Input
                            value={editDiscountName}
                            onChange={(e) => setEditDiscountName(e.target.value)}
                            maxLength={adding ? remainingDiscountNameLength(discount, discountName, modalMaxDiscount, currency) : 100}
                            placeholder="z.B. Frühbucher-Rabatt"
                        />
                    </div>
                    <div>
                        <label>{"Betrag"}</label>
                        <MoneyInput
                            value={editDiscountAmount}
                            onChange={(val) => setEditDiscountAmount(val ?? 0)}
                            min={0}
                            max={modalMaxDiscount}
                            step={0.01}
                            style={{width: "100%"}}
                        />
                        <div style={{color: "#999", fontSize: 12, marginTop: 4}}>
                            {"Maximaler Rabatt: "}
                            <RenderAmount amount={modalMaxDiscount} style={{fontSize: "unset"}} />
                        </div>
                    </div>
                </div>
            </Modal>
        );
    }
};

export default memo(SettledBookingDiscount);
